import React, { useState } from 'react';
import { getBaseUrl } from '../../../../shared/utils/apiConfig';
import { 
  ArrowRight, ArrowLeft, Check, Building2, 
  Settings, Gauge, Users, FileCheck, AlertCircle, Eye, EyeOff
} from 'lucide-react';

/**
 * Onboarding Wizard - Platform Admin
 * 
 * Following 00-READ-THIS-FIRST:
 * - PROGRESSIVE COMPLEXITY: Start simple, enhance as needed
 * - NO MOCK DATA: Uses real /api/tenants POST endpoint
 * - NULL SAFETY: Explicit validation throughout
 * - NO DUPLICACY: Single source of truth for tenant creation
 * - ERROR HANDLING: Graceful degradation
 * - NO TECH DEBT: Clean, maintainable code
 */

interface TenantFormData {
  name: string;
  subdomain: string;
  industry: string;
  company_size: string;
  contact_email: string;
  features: {
    ml_enabled: boolean;
    advanced_correlation: boolean;
    custom_integrations: boolean;
    autonomous_decision: boolean;
  };
  rate_limits: {
    api_calls_per_minute: number;
    events_per_second: number;
    max_concurrent_processes: number;
  };
  admin_user: {
    username: string;
    email: string;
    password: string;
    confirmPassword: string;
  };
}

const INITIAL_FORM_DATA: TenantFormData = {
  name: '',
  subdomain: '',
  industry: '',
  company_size: '',
  contact_email: '',
  features: {
    ml_enabled: true,
    advanced_correlation: true,
    custom_integrations: true,
    autonomous_decision: false,
  },
  rate_limits: {
    api_calls_per_minute: 1000,
    events_per_second: 100,
    max_concurrent_processes: 10,
  },
  admin_user: {
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
  },
};

const STEPS = [
  { id: 1, name: 'Basic Info', icon: Building2 },
  { id: 2, name: 'Features', icon: Settings },
  { id: 3, name: 'Limits', icon: Gauge },
  { id: 4, name: 'Admin User', icon: Users },
  { id: 5, name: 'Review', icon: FileCheck },
];

export const OnboardingWizard: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<TenantFormData>(INITIAL_FORM_DATA);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Password strength validation
  const validatePasswordStrength = (password: string): { isStrong: boolean; message: string } => {
    if (password.length < 12) {
      return { isStrong: false, message: 'Password must be at least 12 characters' };
    }
    if (!/[A-Z]/.test(password)) {
      return { isStrong: false, message: 'Password must contain at least one uppercase letter' };
    }
    if (!/[a-z]/.test(password)) {
      return { isStrong: false, message: 'Password must contain at least one lowercase letter' };
    }
    if (!/[0-9]/.test(password)) {
      return { isStrong: false, message: 'Password must contain at least one number' };
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      return { isStrong: false, message: 'Password must contain at least one special character (!@#$%^&*...)' };
    }
    return { isStrong: true, message: 'Strong password' };
  };

  // NULL SAFETY: Validate each step
  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};

    if (step === 1) {
      if (!formData.name.trim()) newErrors.name = 'Tenant name is required';
      if (!formData.subdomain.trim()) newErrors.subdomain = 'Subdomain is required';
      if (!/^[a-z0-9-]+$/.test(formData.subdomain)) {
        newErrors.subdomain = 'Subdomain must be lowercase alphanumeric with hyphens';
      }
      if (!formData.contact_email.trim()) newErrors.contact_email = 'Contact email is required';
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contact_email)) {
        newErrors.contact_email = 'Invalid email format';
      }
    }

    if (step === 4) {
      if (!formData.admin_user.username.trim()) newErrors.admin_username = 'Username is required';
      if (!formData.admin_user.email.trim()) newErrors.admin_email = 'Email is required';
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.admin_user.email)) {
        newErrors.admin_email = 'Invalid email format';
      }
      if (!formData.admin_user.password) {
        newErrors.admin_password = 'Password is required';
      } else {
        const passwordValidation = validatePasswordStrength(formData.admin_user.password);
        if (!passwordValidation.isStrong) {
          newErrors.admin_password = passwordValidation.message;
        }
      }
      if (!formData.admin_user.confirmPassword) {
        newErrors.admin_confirm_password = 'Please confirm your password';
      } else if (formData.admin_user.password !== formData.admin_user.confirmPassword) {
        newErrors.admin_confirm_password = 'Passwords do not match';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, STEPS.length));
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!validateStep(4)) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const token = localStorage.getItem('astrapeak_platform_admin_token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      // SINGLE SOURCE OF TRUTH: Use platform-admin endpoint
      const response = await fetch(`${getBaseUrl()}/api/platform-admin/tenants`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          config: {
            subdomain: formData.subdomain,
            industry: formData.industry,
            company_size: formData.company_size,
            contact_email: formData.contact_email,
          },
          features: formData.features,
          rate_limits: formData.rate_limits,
          admin_user: {
            username: formData.admin_user.username,
            email: formData.admin_user.email,
            password: formData.admin_user.password
          }
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorDetail = errorData.detail || 'Failed to create tenant';
        
        // Handle specific validation errors
        if (errorDetail.toLowerCase().includes('subdomain')) {
          setErrors({ subdomain: errorDetail });
          setCurrentStep(1); // Go back to Basic Info step
        } else if (errorDetail.toLowerCase().includes('email')) {
          setErrors({ admin_email: errorDetail });
          setCurrentStep(4); // Go back to Admin User step
        } else if (errorDetail.toLowerCase().includes('password')) {
          setErrors({ admin_password: errorDetail });
          setCurrentStep(4); // Go back to Admin User step
        }
        
        throw new Error(errorDetail);
      }

      setSuccess(true);
    } catch (err) {
      console.error('Error creating tenant:', err);
      setSubmitError(err instanceof Error ? err.message : 'Failed to create tenant');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="flex items-center justify-center min-h-[600px]">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="h-8 w-8 text-green-400" />
          </div>
          <h2 className="text-2xl font-semibold text-white mb-2">Tenant Created Successfully!</h2>
          <p className="text-gray-400 mb-6">
            {formData.name} has been onboarded and is ready to use.
          </p>
          <button
            onClick={() => {
              setSuccess(false);
              setFormData(INITIAL_FORM_DATA);
              setCurrentStep(1);
              setShowPassword(false);
              setShowConfirmPassword(false);
            }}
            className="px-6 py-2 bg-primary hover:bg-primary/80 text-white rounded-lg transition-colors"
          >
            Create Another Tenant
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-white mb-2">Create New Tenant</h1>
        <p className="text-gray-400">Complete the setup wizard to onboard a new tenant</p>
      </div>

      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {STEPS.map((step, index) => {
            const Icon = step.icon;
            const isActive = currentStep === step.id;
            const isCompleted = currentStep > step.id;
            
            return (
              <React.Fragment key={step.id}>
                <div className="flex flex-col items-center">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-2 transition-colors ${
                    isCompleted 
                      ? 'bg-green-500/20 text-green-400' 
                      : isActive 
                      ? 'bg-primary text-white' 
                      : 'bg-gray-700 text-gray-400'
                  }`}>
                    {isCompleted ? <Check className="h-6 w-6" /> : <Icon className="h-6 w-6" />}
                  </div>
                  <span className={`text-xs ${isActive ? 'text-white font-medium' : 'text-gray-400'}`}>
                    {step.name}
                  </span>
                </div>
                {index < STEPS.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-4 ${
                    currentStep > step.id ? 'bg-green-500' : 'bg-gray-700'
                  }`} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Form Content */}
      <div className="bg-gray-800 rounded-lg border border-gray-700 p-8 mb-6">
        {/* Step 1: Basic Information */}
        {currentStep === 1 && (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold text-white mb-4">Basic Information</h2>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Tenant Name <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Acme Corporation"
              />
              {errors.name && <p className="text-red-400 text-sm mt-1">{errors.name}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Subdomain <span className="text-red-400">*</span>
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={formData.subdomain}
                  onChange={(e) => setFormData({ ...formData, subdomain: e.target.value.toLowerCase() })}
                  className="flex-1 px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="acme"
                />
                <span className="text-gray-400">-brain.astrapeak.ai</span>
              </div>
              {errors.subdomain && <p className="text-red-400 text-sm mt-1">{errors.subdomain}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Industry</label>
              <select
                value={formData.industry}
                onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">Select industry...</option>
                <option value="financial">Financial Services</option>
                <option value="healthcare">Healthcare</option>
                <option value="technology">Technology</option>
                <option value="retail">Retail</option>
                <option value="manufacturing">Manufacturing</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Company Size</label>
              <div className="grid grid-cols-4 gap-2">
                {['1-50', '51-200', '201-1000', '1000+'].map((size) => (
                  <button
                    key={size}
                    onClick={() => setFormData({ ...formData, company_size: size })}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      formData.company_size === size
                        ? 'bg-primary text-white'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Primary Contact Email <span className="text-red-400">*</span>
              </label>
              <input
                type="email"
                value={formData.contact_email}
                onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="admin@acme.com"
              />
              {errors.contact_email && <p className="text-red-400 text-sm mt-1">{errors.contact_email}</p>}
            </div>
          </div>
        )}

        {/* Step 2: Features */}
        {currentStep === 2 && (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold text-white mb-4">Feature Configuration</h2>
            
            {[
              { key: 'ml_enabled', label: 'ML-Powered Threat Detection', desc: 'Advanced machine learning for anomaly detection', recommended: 'All tenants' },
              { key: 'advanced_correlation', label: 'Advanced Correlation Engine', desc: 'Multi-source event correlation and attack chain detection', recommended: 'Enterprise (200+ employees)' },
              { key: 'custom_integrations', label: 'Custom Integrations', desc: 'API access for custom SIEM connectors', recommended: 'Technical teams' },
              { key: 'autonomous_decision', label: 'Autonomous Decision Engine (Beta)', desc: 'AI-driven automated response to threats', recommended: 'Mature security teams' },
            ].map((feature) => (
              <div
                key={feature.key}
                className={`p-4 rounded-lg border transition-colors cursor-pointer ${
                  formData.features[feature.key as keyof typeof formData.features]
                    ? 'bg-primary/10 border-primary'
                    : 'bg-gray-700/30 border-gray-600 hover:border-gray-500'
                }`}
                onClick={() => setFormData({
                  ...formData,
                  features: {
                    ...formData.features,
                    [feature.key]: !formData.features[feature.key as keyof typeof formData.features]
                  }
                })}
              >
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={formData.features[feature.key as keyof typeof formData.features]}
                    onChange={() => {}}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <h3 className="text-white font-medium mb-1">{feature.label}</h3>
                    <p className="text-gray-400 text-sm mb-1">{feature.desc}</p>
                    <p className="text-gray-500 text-xs">Recommended for: {feature.recommended}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Step 3: Rate Limits */}
        {currentStep === 3 && (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold text-white mb-4">Rate Limits & Quotas</h2>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                API Calls per Minute
              </label>
              <input
                type="number"
                value={formData.rate_limits.api_calls_per_minute}
                onChange={(e) => setFormData({
                  ...formData,
                  rate_limits: { ...formData.rate_limits, api_calls_per_minute: parseInt(e.target.value) || 0 }
                })}
                className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <p className="text-gray-500 text-sm mt-1">Recommended: 1000 for Enterprise</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Events per Second
              </label>
              <input
                type="number"
                value={formData.rate_limits.events_per_second}
                onChange={(e) => setFormData({
                  ...formData,
                  rate_limits: { ...formData.rate_limits, events_per_second: parseInt(e.target.value) || 0 }
                })}
                className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Max Concurrent Processes
              </label>
              <input
                type="number"
                value={formData.rate_limits.max_concurrent_processes}
                onChange={(e) => setFormData({
                  ...formData,
                  rate_limits: { ...formData.rate_limits, max_concurrent_processes: parseInt(e.target.value) || 0 }
                })}
                className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>
        )}

        {/* Step 4: Admin User */}
        {currentStep === 4 && (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold text-white mb-4">Create Admin User</h2>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Username <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={formData.admin_user.username}
                onChange={(e) => setFormData({
                  ...formData,
                  admin_user: { ...formData.admin_user, username: e.target.value }
                })}
                className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="acme-admin"
              />
              {errors.admin_username && <p className="text-red-400 text-sm mt-1">{errors.admin_username}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Email <span className="text-red-400">*</span>
              </label>
              <input
                type="email"
                value={formData.admin_user.email}
                onChange={(e) => setFormData({
                  ...formData,
                  admin_user: { ...formData.admin_user, email: e.target.value }
                })}
                className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="admin@acme.com"
              />
              {errors.admin_email && <p className="text-red-400 text-sm mt-1">{errors.admin_email}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Password <span className="text-red-400">*</span>
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={formData.admin_user.password}
                  onChange={(e) => setFormData({
                    ...formData,
                    admin_user: { ...formData.admin_user, password: e.target.value }
                  })}
                  className="w-full px-4 py-2 pr-12 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-300 transition-colors"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              {errors.admin_password && <p className="text-red-400 text-sm mt-1">{errors.admin_password}</p>}
              {formData.admin_user.password && !errors.admin_password && validatePasswordStrength(formData.admin_user.password).isStrong && (
                <p className="text-green-400 text-sm mt-1 flex items-center gap-1">
                  <Check className="h-4 w-4" /> {validatePasswordStrength(formData.admin_user.password).message}
                </p>
              )}
              <p className="text-gray-500 text-xs mt-2">
                Password must be at least 12 characters with uppercase, lowercase, number, and special character
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Confirm Password <span className="text-red-400">*</span>
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  value={formData.admin_user.confirmPassword}
                  onChange={(e) => setFormData({
                    ...formData,
                    admin_user: { ...formData.admin_user, confirmPassword: e.target.value }
                  })}
                  className="w-full px-4 py-2 pr-12 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-300 transition-colors"
                  aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                >
                  {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              {errors.admin_confirm_password && <p className="text-red-400 text-sm mt-1">{errors.admin_confirm_password}</p>}
              {formData.admin_user.confirmPassword && formData.admin_user.password === formData.admin_user.confirmPassword && (
                <p className="text-green-400 text-sm mt-1 flex items-center gap-1">
                  <Check className="h-4 w-4" /> Passwords match
                </p>
              )}
            </div>
          </div>
        )}

        {/* Step 5: Review */}
        {currentStep === 5 && (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold text-white mb-4">Review Configuration</h2>
            
            <div className="space-y-4">
              <div className="bg-gray-700/30 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-white font-medium">Basic Information</h3>
                  <button
                    onClick={() => setCurrentStep(1)}
                    className="text-primary hover:text-primary/80 text-sm"
                  >
                    Edit
                  </button>
                </div>
                <div className="space-y-2 text-sm">
                  <p className="text-gray-300">• Name: {formData.name}</p>
                  <p className="text-gray-300">• URL: {formData.subdomain}-brain.astrapeak.ai</p>
                  {formData.industry && <p className="text-gray-300">• Industry: {formData.industry}</p>}
                  {formData.company_size && <p className="text-gray-300">• Size: {formData.company_size} employees</p>}
                </div>
              </div>

              <div className="bg-gray-700/30 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-white font-medium">Features Enabled</h3>
                  <button
                    onClick={() => setCurrentStep(2)}
                    className="text-primary hover:text-primary/80 text-sm"
                  >
                    Edit
                  </button>
                </div>
                <div className="space-y-2 text-sm">
                  {formData.features.ml_enabled && <p className="text-gray-300">• ML-Powered Threat Detection</p>}
                  {formData.features.advanced_correlation && <p className="text-gray-300">• Advanced Correlation Engine</p>}
                  {formData.features.custom_integrations && <p className="text-gray-300">• Custom Integrations</p>}
                  {formData.features.autonomous_decision && <p className="text-gray-300">• Autonomous Decision Engine</p>}
                </div>
              </div>

              <div className="bg-gray-700/30 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-white font-medium">Resource Limits</h3>
                  <button
                    onClick={() => setCurrentStep(3)}
                    className="text-primary hover:text-primary/80 text-sm"
                  >
                    Edit
                  </button>
                </div>
                <div className="space-y-2 text-sm">
                  <p className="text-gray-300">• API Calls: {formData.rate_limits.api_calls_per_minute}/min</p>
                  <p className="text-gray-300">• Events: {formData.rate_limits.events_per_second}/sec</p>
                  <p className="text-gray-300">• Processes: {formData.rate_limits.max_concurrent_processes}</p>
                </div>
              </div>

              <div className="bg-gray-700/30 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-white font-medium">Admin User</h3>
                  <button
                    onClick={() => setCurrentStep(4)}
                    className="text-primary hover:text-primary/80 text-sm"
                  >
                    Edit
                  </button>
                </div>
                <div className="space-y-2 text-sm">
                  <p className="text-gray-300">• Username: {formData.admin_user.username}</p>
                  <p className="text-gray-300">• Email: {formData.admin_user.email}</p>
                </div>
              </div>
            </div>

            {submitError && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-red-400 font-medium">Error Creating Tenant</p>
                  <p className="text-red-400/80 text-sm mt-1">{submitError}</p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Navigation Buttons */}
      <div className="flex items-center justify-between">
        <button
          onClick={handleBack}
          disabled={currentStep === 1}
          className="flex items-center gap-2 px-6 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>

        {currentStep < STEPS.length ? (
          <button
            onClick={handleNext}
            className="flex items-center gap-2 px-6 py-2 bg-primary hover:bg-primary/80 text-white rounded-lg transition-colors"
          >
            Next
            <ArrowRight className="h-4 w-4" />
          </button>
        ) : (
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="flex items-center gap-2 px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Creating...' : 'Create Tenant'}
            <Check className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
};

export default OnboardingWizard;
